import { queryClient } from '@/lib/react-query/common/queryClient';
import { ApiService } from '@/lib/api';
import {
  auth,
  googleProvider,
  microsoftProvider,
  primeFirebaseToken,
} from '@/lib/firebase';
import type { UserProfile } from '@/lib/models';
import {
  onIdTokenChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { createContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AuthContextType } from './types';

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export type { AuthContextType } from './types';
export { useAuth } from './hooks/useAuth';

export function verificationPageOwnsProfileSync(location: {
  pathname: string;
  search: string;
}): boolean {
  return (
    location.pathname === '/verify-email' &&
    new URLSearchParams(location.search).has('oobCode')
  );
}

/**
 * Refresh callers historically treat profile synchronisation as best-effort.
 * Keep that contract here so a transient API failure cannot strand a form in
 * its saving state after the underlying profile mutation has already succeeded.
 */
export async function safelySyncUserProfile(
  syncUser: () => Promise<{
    success: boolean;
    value?: { data?: UserProfile } | null;
  }> = ApiService.User.syncUser,
): Promise<UserProfile | null> {
  try {
    const response = await syncUser();
    return response.success && response.value?.data
      ? response.value.data
      : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const identity = useRef<string | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [sessionToken, setSessionToken] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSSO, setIsSSO] = useState(false);
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [verificationEmailStatus, setVerificationEmailStatus] = useState<
    'idle' | 'sent' | 'failed'
  >('idle');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const unifiedClinicianJourney =
    import.meta.env.VITE_CLINICIAN_SELF_SIGNUP_V2_ENABLED === 'true';

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async firebaseUser => {
      const uid = firebaseUser?.uid ?? null;
      if (identity.current !== uid) {
        queryClient.clear();
        setUser(null);
        setIsAuthenticated(false);
        identity.current = uid;
      }
      if (firebaseUser) {
        try {
          // Get the token and prime the cache before calling syncUser so the
          // Authorization header is available when makeApiRequest reads it.
          const token = await firebaseUser.getIdToken();
          if (identity.current !== uid) return;
          primeFirebaseToken(token);
          setPendingEmail(firebaseUser.email);
          const currentLocation = locationRef.current;
          if (
            unifiedClinicianJourney &&
            ['/invite', '/clinician-invite'].includes(currentLocation.pathname)
          ) {
            // The invite page owns identity linking. Syncing before its signed
            // token is accepted could bind an identity by email prematurely.
            setIsLoading(false);
            return;
          }
          // Applying a Firebase email-verification code forces a token refresh
          // and then syncs the profile in UnifiedVerifyEmail. If this global
          // observer races that flow with the pre-refresh token, /user/sync
          // can briefly return 404 and sign the clinician out just as the
          // verification page is sending them to Clinician Details.
          if (
            unifiedClinicianJourney &&
            verificationPageOwnsProfileSync(currentLocation)
          ) {
            setIsLoading(false);
            return;
          }
          if (unifiedClinicianJourney && firebaseUser.emailVerified === false) {
            setRegistrationRequired(false);
            setIsLoading(false);
            navigate('/verify-email', { replace: true });
            return;
          }
          const response = await ApiService.User.syncUser();
          if (identity.current !== uid) return;
          if (response.success && response.value?.data) {
            setUser(response.value.data);
            setIsAuthenticated(true);
            setRegistrationRequired(false);
            setIsSSO(
              !firebaseUser.providerData.some(p => p.providerId === 'password'),
            );
          } else {
            const code = response.failure ? response.code : undefined;

            // Only sign out when the user truly doesn't exist in the DB (404).
            // Network errors (code 0), server errors (5xx), or other transient
            // failures should not log the user out — keep the existing session.
            if (unifiedClinicianJourney && code === 409) {
              setRegistrationRequired(true);
              setIsAuthenticated(false);
              navigate('/signup/details', { replace: true });
            } else if (code === 404) {
              const googleOrMicrosoft = firebaseUser.providerData.some(
                p =>
                  p.providerId === 'google.com' ||
                  p.providerId === 'microsoft.com',
              );
              // Retain the provider token until authenticated registration finishes.
              if (!googleOrMicrosoft) await signOut(auth);
              setUser(null);
              setIsAuthenticated(false);

              // SSO user with no DB record — redirect to signup pre-filled
              const isSSO = firebaseUser.providerData.some(
                p =>
                  p.providerId === 'google.com' ||
                  p.providerId === 'microsoft.com',
              );
              if (isSSO) {
                const params = new URLSearchParams({ sso: '1' });
                if (firebaseUser.email) params.set('email', firebaseUser.email);
                if (firebaseUser.displayName) {
                  const parts = firebaseUser.displayName.trim().split(/\s+/);
                  params.set('firstName', parts[0] ?? '');
                  params.set('lastName', parts.slice(1).join(' ') ?? '');
                }
                navigate(`/signup?${params.toString()}`);
              }
            }
            // For any other failure (network, 5xx) — do nothing, stay authenticated
          }
        } catch {
          // Unexpected failures may be transient. Preserve the Firebase session
          // and let the current screen offer an explicit retry.
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setRegistrationRequired(false);
        setPendingEmail(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, unifiedClinicianJourney]);

  const login = async (email: string, password: string) => {
    // Throws on bad credentials — caller is responsible for catching
    await signInWithEmailAndPassword(auth, email, password);
    // onIdTokenChanged will handle syncUser and state updates
  };

  const loginWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
    // onIdTokenChanged will handle syncUser and state updates
  };

  const loginWithMicrosoft = async () => {
    await signInWithPopup(auth, microsoftProvider);
    // onIdTokenChanged will handle syncUser and state updates
  };

  const createEmailAccount = async (email: string, password: string) => {
    setVerificationEmailStatus('idle');
    const credential = await createUserWithEmailAndPassword(
      auth,
      email.trim().toLowerCase(),
      password,
    );
    const token = await credential.user.getIdToken();
    primeFirebaseToken(token);
    setPendingEmail(credential.user.email);
    try {
      await ApiService.User.sendRegistrationVerificationEmail();
      setVerificationEmailStatus('sent');
    } catch {
      setVerificationEmailStatus('failed');
      // The identity already exists and the verification screen has a working
      // resend action. Do not strand the clinician on a second create attempt.
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setIsAuthenticated(false);
    setIsSSO(false);
    setRegistrationRequired(false);
    setPendingEmail(null);
    setVerificationEmailStatus('idle');
    setSessionToken('');
    navigate('/login', { replace: true });
  };

  const updateUser = async () => {
    const requestingIdentity = identity.current;
    const refreshedUser = await safelySyncUserProfile();
    if (!requestingIdentity || identity.current !== requestingIdentity)
      return false;
    if (!refreshedUser) return false;
    setUser(refreshedUser);
    setIsAuthenticated(true);
    setRegistrationRequired(false);
    setIsSSO(
      !!auth.currentUser &&
        !auth.currentUser.providerData.some(p => p.providerId === 'password'),
    );
    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        sessionToken,
        setSessionToken,
        login,
        loginWithGoogle,
        loginWithMicrosoft,
        createEmailAccount,
        logout,
        updateUser,
        isAuthenticated,
        isLoading,
        isSSO,
        registrationRequired,
        pendingEmail,
        verificationEmailStatus,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
